import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { ApiService, TranscriptAssetType } from '../../client';
import { MediaRegistry } from '../registries/MediaRegistry';
import type { EnhancedToolResult, ImageContent, ToolExecutionContext } from '../types';
import { Tool } from './Tool';

import { extractTranscript } from 'js/src/agent/video/extractTranscript';
import {
    encodeVideoAsTileStrip,
    type TileTranscriptWord,
} from 'js/src/agent/video/video-tile-strip';
import { VideoDecoder } from 'js/src/agent/video/videoDecoder';

export const analyzeVideoSchema = z.object({
    media_id: z.string().describe('The id of the video to analyze.'),
    language_code: z
        .string()
        .optional()
        .describe(
            'Optional language code for transcription when no transcript is cached (e.g. "en").',
        ),
});
export type AnalyzeVideoArgs = z.infer<typeof analyzeVideoSchema>;

/** Sanity ceiling on video length (3h — covers full game broadcasts and long podcasts, guards against
 *  pathological inputs). Tokens don't scale with duration: ≤60 sampled frames (sampleFrames'
 *  MAX_SAMPLES) bound the tile count regardless of length. */
const HARD_DURATION_CAP_S = 10800;

/**
 * Frame extraction reads video pixels back off a canvas, which throws a
 * SecurityError unless the source sends CORS headers. Our own GCS media
 * (uploads / stock / source, served from `storage.googleapis.com/<bucket>`) is
 * CORS-enabled; same-origin media is trivially readable. Externally-hosted
 * media (connected DAM assets on third-party CDNs) is not, so we reject it up
 * front rather than let the pipeline fail late on a tainted canvas.
 *
 * Our GCS buckets follow two naming conventions (per the terraform): most are
 * prefixed — the `lumen5-${bit}` module buckets (`lumen5-prod-video`, ...) and
 * the per-user `lumen5-user-<uuid>` buckets — while the transient uploads
 * buckets are domain-named (`storage.lumen5.com`, `storage-dev.lumen5.com`).
 * We accept both rather than listing bucket names. The domain-named form is
 * spoof-proof: GCS only lets you name a bucket `*.lumen5.com` if you've
 * domain-verified ownership of lumen5.com, so a third party can't mint one.
 */
export function isAnalyzableMediaUrl(url: string): boolean {
    if (url.startsWith('data:')) return true;
    let parsed: URL;
    try {
        parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    } catch {
        return false;
    }
    if (typeof window !== 'undefined' && parsed.host === window.location.host) {
        return true;
    }
    if (parsed.hostname === 'storage.googleapis.com') {
        const bucket = parsed.pathname.split('/').filter(Boolean)[0] ?? '';
        return bucket.startsWith('lumen5') || bucket.endsWith('.lumen5.com');
    }
    return false;
}

/**
 * Analyzes a video already referenced in the conversation (by UUID) and returns
 * its transcript plus a timestamped, shot-segmented "tile strip" visual encoding
 * — interleaved text + image parts that give the model first-hand insight into
 * the video's content, pacing, and style. All processing happens in the browser
 * (frame extraction via canvas readback); the transcript comes from the cached
 * asset transcript, falling back to on-the-fly Whisper transcription.
 */
export class AnalyzeVideoTool extends Tool<typeof analyzeVideoSchema> {
    protected override readonly schema = analyzeVideoSchema;
    protected override readonly toolName = TOOL_NAMES.analyzeVideo;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Analyze a video that is referenced in the conversation and return its ' +
        'transcript together with a timestamped, shot-by-shot tile-strip visual ' +
        "encoding. Use this to understand a video's content, pacing, and visual style " +
        'before answering questions about it or transferring its style. Works on ' +
        "videos in the user's Lumen5 library (uploads and stock footage). Do NOT call " +
        'it on externally-hosted media such as connected DAM assets (Brandfolder, ' +
        'Getty, Storyblocks, etc.) — those cannot be read for analysis.';

    public getToolDisplayName(): string {
        return 'Analyzing video';
    }

    public get statusIcon(): string {
        return 'movie';
    }

    public async execute(
        args: AnalyzeVideoArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { media_id: registryId, language_code } = args;

        const mediaRegistry = MediaRegistry.getInstance();
        if (!mediaRegistry.hasItem(registryId)) {
            return {
                result: `Error: No video with id "${registryId}" found. Only videos referenced in this conversation can be analyzed.`,
                isSuccess: false,
            };
        }

        const video = mediaRegistry.getItem(registryId)?.background?.video;
        if (!video) {
            return {
                result: `Error: Media "${registryId}" is not a video.`,
                isSuccess: false,
            };
        }

        // The transcript and frame lookups key off the asset uuid, which the registry entry
        // carries; the model only ever sees the registry id.
        const uuid = video.uuid;
        if (!uuid) {
            return {
                result: `Error: Media "${registryId}" has no asset uuid to analyze.`,
                isSuccess: false,
            };
        }

        const sourceUrl = video.render_url ?? video.preview_url;
        if (!sourceUrl) {
            return {
                result: `Error: Video "${uuid}" has no playable URL to analyze.`,
                isSuccess: false,
            };
        }

        if (!isAnalyzableMediaUrl(sourceUrl)) {
            return {
                result: `Error: Video "${uuid}" is hosted externally and can't be analyzed — ${TOOL_NAMES.analyzeVideo} only works on videos in your Lumen5 library (uploads and stock footage), not connected DAM or third-party media.`,
                isSuccess: false,
            };
        }

        const decoder = new VideoDecoder();
        try {
            const meta = await decoder.loadUrl(sourceUrl);
            if (!Number.isFinite(meta.durationS) || meta.durationS <= 0) {
                return {
                    result: `Error: Could not read a valid duration for video "${uuid}" — it may be corrupt or still processing.`,
                    isSuccess: false,
                };
            }
            if (meta.durationS > HARD_DURATION_CAP_S) {
                return {
                    result: `Error: Video is ${meta.durationS.toFixed(1)}s, which exceeds the ${HARD_DURATION_CAP_S}s analysis limit. Trim it to a shorter representative segment first.`,
                    isSuccess: false,
                };
            }

            const { words, languageCode } = await this.resolveTranscript(
                uuid,
                sourceUrl,
                video.has_audio === true || video.has_voiceover === true,
                language_code,
            );

            const resultContent = await encodeVideoAsTileStrip({
                decoder,
                meta: {
                    durationS: meta.durationS,
                    widthPx: meta.widthPx,
                    heightPx: meta.heightPx,
                    languageCode,
                },
                transcriptWords: words,
            });

            const shotStrips = resultContent.filter(
                (part): part is ImageContent => part.type === 'image_url',
            ).length;
            const name = video.title ?? uuid;
            return {
                result: `Analyzed "${name}": ${meta.durationS.toFixed(1)}s across ${shotStrips} tile strip(s), transcript ${words.length > 0 ? 'included' : 'unavailable'}.`,
                resultContent,
                isSuccess: true,
            };
        } finally {
            decoder.dispose();
        }
    }

    private async resolveTranscript(
        uuid: string,
        sourceUrl: string,
        hasAudio: boolean,
        languageCode?: string,
    ): Promise<{ words: TileTranscriptWord[]; languageCode: string | null }> {
        // Prefer the cached asset transcript (server-side Whisper, already aligned).
        try {
            const { data } = await ApiService.assetTranscriptRetrieve({
                path: { asset_id: uuid },
                query: { asset_type: TranscriptAssetType.VIDEO },
            });
            if (data.transcriptWords.length > 0) {
                return {
                    words: data.transcriptWords.map((w) => ({
                        word: w.word,
                        startTime: w.startTime,
                        endTime: w.endTime,
                    })),
                    languageCode: data.language ?? null,
                };
            }
        } catch {
            // No cached transcript — fall through to on-the-fly transcription.
        }

        if (hasAudio) {
            // Only the fallback needs the raw bytes (to re-transcribe via Whisper).
            // Editor media is CORS-enabled, so fetch the URL directly — no proxy.
            const response = await fetch(sourceUrl);
            if (!response.ok) {
                return { words: [], languageCode: null };
            }
            const blob = await response.blob();
            const file = new File([blob], `${uuid}.mp4`, {
                type: blob.type || 'video/mp4',
            });
            const transcript = await extractTranscript(file, languageCode);
            if (transcript && transcript.words.length > 0) {
                return {
                    words: transcript.words.map((w) => ({
                        word: w.w,
                        startTime: w.s,
                        endTime: w.e,
                    })),
                    languageCode: transcript.languageCode,
                };
            }
        }

        return { words: [], languageCode: null };
    }
}
